import { createSignal, createEffect, createMemo, createSelector, on, Show, For, onMount, onCleanup, untrack, type Component, type JSX } from "solid-js";
import * as d3 from "d3";
import { persistantStore } from "../../../store/persistantStore";
import { sourcesStore } from "../../../store/sourcesStore";
import Loading from "../../../components/utilities/Loading";
import DropDownButton from "../../../components/buttons/DropDownButton";
import ScatterTimeseries from "../../../components/charts/ScatterTimeseries";
import { buildColorGrouping } from "../../../utils/colorGrouping";
import { chromaticCellHaloClass } from "../../../utils/chromaticTextReadability";
import {
  cheatSheetTwsByConfigTableHint,
  cheatSheetTwsBinColumnHeader,
  dailyTrainingManeuverFilterExplanation,
  dailyTrainingStraightLineFilterExplanation,
} from "../helpers/cheatSheetColumnLabels";
import {
  trainingSummaryAccelerationColumnOrder,
  trainingSummaryAccelerationTableHeader,
  trainingSummaryManeuverColumnOrderForType,
  trainingSummaryManeuverTableHeader,
  trainingSummaryStraightColumnOrder,
  trainingSummaryStraightTableHeader,
} from "../helpers/trainingSummaryTableColumns";
import {
  fetchDailyTrainingSettings,
  fetchDailyTrainingCoverageTimeline,
  dedupeDailyTrainingRowsBySource,
  filterDailyTrainingRowsForDatasetView,
  filterDailyTrainingRowsForDayView,
  fleetDateRangeReady,
  formatDailySettingsTwsBinDropdownLabel,
  formatDailySettingsTwsBinRangeHint,
  formatBrushDateRange,
  formatSummaryTablesAppliedFiltersHint,
  summaryTablesAppliedFilterParts,
  mapCoveragePointsToTimelinePoints,
  mapTrainingScatterToTimelinePoints,
  twsBinCenterFromDropdownLabel,
  type DailyTrainingCoveragePoint,
  type DailyTrainingLegType,
  type DailyTrainingManeuverType,
  type DailyTrainingScatterPoint,
  type DailyTrainingSettingsView,
  type SummaryTablesHistoryFilters,
} from "../helpers/dailyTrainingSettingsApi";
import PivotedMetricTable from "../helpers/PivotedMetricTable";
import { logActivity, getCurrentProjectDatasetIds } from "../../../utils/logging";
import { error as logError } from "../../../utils/console";
import {
  SPEED_UNIT_KPH,
  type SpeedDisplayUnit,
} from "../../../utils/speedUnits";

const TWS_ALL_LABEL = "ALL";
/** GP50 fleet Summary Tables: kph bin centers (step 5), independent of user speed display preference. */
const FLEET_TWS_BINS = [10, 15, 20, 25, 30, 35, 40, 45, 50, 55] as const;
/** Project Summary Tables (fleet mode): no ALL option; default bin center in kph. */
const FLEET_DEFAULT_TWS_BIN = 25;

/** In-memory unbrushed table payload for the current PoS/TWS/filter context. */
interface BaselineTableCache {
  cacheKey: string;
  straightRows: Record<string, unknown>[];
  /** Maneuver rows keyed by maneuver type — straight-line cache is shared across types. */
  maneuverRowsByType: Partial<Record<DailyTrainingManeuverType, Record<string, unknown>[]>>;
  /** Acceleration rows — point-of-sail driven, shared across maneuver types like straight-line. */
  accelerationRows: Record<string, unknown>[];
  twsBins: number[];
}

function getTeamCellStyle(sourceName: string | null | undefined): string | undefined {
  if (sourceName == null || String(sourceName).trim() === "") {return undefined;}
  const color = sourcesStore.getSourceColor(String(sourceName));
  if (!color) {return undefined;}
  return `color: ${color};`;
}

function getTeamCellHaloClass(sourceName: string | null | undefined): string {
  if (sourceName == null || String(sourceName).trim() === "") {return "";}
  const color = sourcesStore.getSourceColor(String(sourceName));
  return chromaticCellHaloClass(color ?? null, { sourceLabel: sourceName });
}

function fleetDefaultTwsLabel(available: number[]): string {
  if (available.includes(FLEET_DEFAULT_TWS_BIN)) {
    return formatDailySettingsTwsBinDropdownLabel(FLEET_DEFAULT_TWS_BIN);
  }
  if (available.length > 0) {
    return formatDailySettingsTwsBinDropdownLabel(available[0]);
  }
  return formatDailySettingsTwsBinDropdownLabel(FLEET_DEFAULT_TWS_BIN);
}

const { selectedClassName, selectedProjectId, selectedDate, selectedDatasetId, selectedSourceId } = persistantStore;

/** GP50 Summary Tables: source data and labels are always kph (no unit conversion). */
function summaryTablesSpeedUnit(): SpeedDisplayUnit {
  return SPEED_UNIT_KPH;
}

function formatNum(value: number): string {
  if (Number.isNaN(value)) {return "—";}
  return value.toFixed(1);
}

function getRowValue(row: Record<string, unknown>, key: string): number | string | null {
  const v = row[key] ?? row[key.toLowerCase()] ?? row[key.toUpperCase()];
  if (v == null || v === "") {return null;}
  const n = Number(v);
  if (!Number.isNaN(n)) {return n;}
  return String(v);
}

function valueKeysFromRows(
  rows: Record<string, unknown>[],
  rowLabelKey: string,
  hideTws: boolean
): string[] {
  if (rows.length === 0) {return [];}
  const keys = new Set(
    Object.keys(rows[0])
      .filter((k) => k !== undefined && k !== null && String(k).trim() !== "")
      .map((k) => k.toLowerCase())
  );
  const label = rowLabelKey.toLowerCase();
  return [...keys].filter((k) => {
    if (k === label || k === "count") {return false;}
    if (hideTws && k === "tws") {return false;}
    return true;
  });
}

function recomputeScalesFromData(
  rowsList: Record<string, unknown>[],
  formattedKeys: string[],
  vKeys: string[]
): Record<string, string> {
  const newScales: Record<string, string> = {};
  for (const key of formattedKeys) {
    if (!vKeys.includes(key) || rowsList.length === 0) {continue;}
    const values = rowsList
      .map((r) => getRowValue(r, key))
      .filter((v): v is number => typeof v === "number" && !Number.isNaN(v));
    const min = d3.min(values) ?? 0;
    const max = d3.max(values) ?? 0;
    newScales[key] = JSON.stringify({ domain: [min, max], range: [0, 6] });
  }
  return newScales;
}

/** Fleet Summary: show copy/export control after hovering a table this long (ms). */
const FLEET_TABLE_EXPORT_HOVER_MS = 5000;

function buildSettingsTableColumns(
  rows: Record<string, unknown>[],
  rowLabelKey: string,
  columnOrder: readonly string[],
  opts?: { hideTwsColumn?: boolean; strictColumnOrder?: boolean; includeCount?: boolean }
): string[] {
  const label = rowLabelKey.toLowerCase();
  const keys = new Set(
    rows.length > 0
      ? Object.keys(rows[0])
          .filter((k) => k !== undefined && k !== null && String(k).trim() !== "")
          .map((k) => k.toLowerCase())
      : []
  );
  const includeCount = opts?.includeCount !== false;

  if (opts?.strictColumnOrder) {
    const labelPresent = label && (keys.has(label) || rows.length > 0) ? [label] : [];
    const twsExtra =
      !opts.hideTwsColumn && label !== "tws" && (keys.has("tws") || columnOrder.includes("tws"))
        ? ["tws"]
        : [];
    const metrics = columnOrder.filter(
      (k) => k !== label && k !== "tws" && k !== "count"
    );
    const tail =
      includeCount && (columnOrder.includes("count") || keys.has("count")) ? (["count"] as const) : [];
    return [...labelPresent, ...twsExtra, ...metrics, ...tail];
  }

  if (rows.length === 0) {
    return [];
  }
  const preferred = columnOrder.filter((k) => keys.has(k));
  const rest = [...keys]
    .filter((k) => k !== label && k !== "tws" && k !== "count" && !columnOrder.includes(k))
    .sort((a, b) => a.localeCompare(b));
  const tail = includeCount && keys.has("count") ? (["count"] as const) : [];
  const labelPresent = keys.has(label) ? [label] : [];
  const twsExtra =
    !opts?.hideTwsColumn && label !== "tws" && keys.has("tws") ? ["tws"] : [];
  return [...labelPresent, ...twsExtra, ...preferred, ...rest, ...tail];
}

function sortSettingsTableRows(
  rows: Record<string, unknown>[],
  sortConfig: { key: string; direction: "asc" | "desc" }
): Record<string, unknown>[] {
  const { key, direction } = sortConfig;
  if (!key || rows.length === 0) {
    return rows;
  }
  const getVal = (row: Record<string, unknown>) => getRowValue(row, key);
  return [...rows].sort((a, b) => {
    const aV = getVal(a);
    const bV = getVal(b);
    if (aV == null && bV == null) {
      return 0;
    }
    if (aV == null) {
      return direction === "asc" ? 1 : -1;
    }
    if (bV == null) {
      return direction === "asc" ? -1 : 1;
    }
    const cmp =
      typeof aV === "number" && typeof bV === "number" ? aV - bV : String(aV).localeCompare(String(bV));
    return direction === "asc" ? cmp : -cmp;
  });
}

/**
 * TSV for the summary tables' "Copy Table Data". Follows the on-screen orientation so a
 * paste into Excel matches what the user is looking at.
 */
export function buildSettingsTableClipboardText(
  rows: Record<string, unknown>[],
  rowLabelKey: string,
  columnOrder: readonly string[],
  getHeader: (key: string) => string,
  sortConfig: { key: string; direction: "asc" | "desc" },
  opts?: { hideTwsColumn?: boolean; strictColumnOrder?: boolean; includeCount?: boolean; pivoted?: boolean }
): string {
  const cols = buildSettingsTableColumns(rows, rowLabelKey, columnOrder, opts);
  const rowsList = sortSettingsTableRows(rows, sortConfig);
  const cellText = (row: Record<string, unknown>, key: string): string => {
    const val = getRowValue(row, key);
    if (val == null) {
      return "—";
    }
    const lk = key.toLowerCase();
    if (lk === rowLabelKey.toLowerCase() || lk === "tws" || lk === "count") {
      return String(val);
    }
    return typeof val === "number" ? formatNum(val) : String(val);
  };

  if (opts?.pivoted) {
    // One column per source, one line per metric — the transpose of the default.
    const metricKeys = cols.filter((k) => k.toLowerCase() !== rowLabelKey.toLowerCase());
    let text =
      [getHeader(rowLabelKey), ...rowsList.map((row) => cellText(row, rowLabelKey))].join("\t") + "\n";
    for (const key of metricKeys) {
      text += [getHeader(key), ...rowsList.map((row) => cellText(row, key))].join("\t") + "\n";
    }
    return text;
  }

  let text = cols.map((k) => getHeader(k)).join("\t") + "\n";
  for (const row of rowsList) {
    text += cols.map((key) => cellText(row, key)).join("\t") + "\n";
  }
  return text;
}

async function copySettingsTableToClipboard(
  rows: Record<string, unknown>[],
  rowLabelKey: string,
  columnOrder: readonly string[],
  getHeader: (key: string) => string,
  sortConfig: { key: string; direction: "asc" | "desc" },
  opts?: { hideTwsColumn?: boolean; strictColumnOrder?: boolean; includeCount?: boolean; pivoted?: boolean }
): Promise<void> {
  const clipboardText = buildSettingsTableClipboardText(
    rows,
    rowLabelKey,
    columnOrder,
    getHeader,
    sortConfig,
    opts
  );

  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(clipboardText);
    return;
  }
  const ta = document.createElement("textarea");
  ta.value = clipboardText;
  ta.style.position = "fixed";
  ta.style.left = "-9999px";
  document.body.appendChild(ta);
  ta.select();
  document.execCommand("copy");
  document.body.removeChild(ta);
}

interface SettingsTableProps {
  rows: () => Record<string, unknown>[];
  rowLabelKey: string;
  columnOrder: readonly string[];
  getHeader: (key: string) => string;
  sortConfig: () => { key: string; direction: "asc" | "desc" };
  setSortConfig: (c: { key: string; direction: "asc" | "desc" }) => void;
  columnScales: () => Record<string, string>;
  setColumnScales: (s: Record<string, string>) => void;
  formattedColumns: () => string[];
  setFormattedColumns: (cols: string[]) => void;
  showDeltas: () => boolean;
  emptyMessage: string;
  /** Day view: TWS is fixed by the dropdown — hide the redundant column. */
  hideTwsColumn?: boolean;
  /** When true, only render configured columnOrder keys (no extra row keys). */
  strictColumnOrder?: boolean;
  /** Sample-size column. Straight line omits it; the column order already ends at JIB LEAD. */
  includeCount?: boolean;
  /**
   * Pivoted layout: metrics down the left, row-label values (sources) as columns. Column
   * order follows the same sortConfig as the unpivoted table, so the ranking is preserved;
   * the sort affordance moves from the column headers to the metric row headers.
   */
  pivoted?: () => boolean;
}

const TableLoadingPlaceholder: Component = () => (
  <div class="cheat-sheet-table-loading-wrapper">
    <Loading fullScreen={false} compact={true} message="" />
  </div>
);

const SettingsTable: Component<SettingsTableProps> = (props) => {
  const isRowLabelKey = createSelector(() => props.rowLabelKey.toLowerCase());

  const tableColumns = createMemo(() =>
    buildSettingsTableColumns(props.rows(), props.rowLabelKey, props.columnOrder, {
      hideTwsColumn: props.hideTwsColumn,
      strictColumnOrder: props.strictColumnOrder,
      includeCount: props.includeCount,
    })
  );

  const sortedRows = createMemo(() =>
    sortSettingsTableRows(props.rows(), props.sortConfig())
  );

  const valueKeys = createMemo(() =>
    tableColumns().filter((k) => {
      const lk = k.toLowerCase();
      return lk !== props.rowLabelKey.toLowerCase() && lk !== "tws" && lk !== "count";
    })
  );

  const isPivoted = (): boolean => props.pivoted?.() === true;

  /** Pivoted row axis: every column except the row label (TWS/COUNT stay as ordinary metric rows). */
  const pivotMetricKeys = createMemo(() =>
    tableColumns().filter((k) => k.toLowerCase() !== props.rowLabelKey.toLowerCase())
  );

  const cellClassForScale = (
    row: Record<string, unknown>,
    key: string,
    scales: Record<string, string>,
    formatted: string[]
  ): string => {
    if (!formatted.includes(key) || !scales[key]) {return "";}
    try {
      const { domain, range } = JSON.parse(scales[key]) as { domain: number[]; range: number[] };
      const scale = d3.scaleLinear().domain(domain).range(range);
      const v = getRowValue(row, key);
      if (v == null || typeof v !== "number" || Number.isNaN(v)) {return "";}
      const x = scale(v);
      return `c${Math.round(Math.max(0, Math.min(6, x)))}`;
    } catch {
      return "";
    }
  };

  const sortData = (key: string) => {
    const current = props.sortConfig();
    const direction = current.key === key && current.direction === "asc" ? "desc" : "asc";
    props.setSortConfig({ key, direction });
    const vk = valueKeys();
    if (vk.includes(key)) {
      const rowsList = props.rows();
      const values = rowsList
        .map((r) => getRowValue(r, key))
        .filter((v): v is number => typeof v === "number" && !Number.isNaN(v));
      const min = d3.min(values) ?? 0;
      const max = d3.max(values) ?? 0;
      const scaleStr = JSON.stringify({ domain: [min, max], range: [0, 6] });
      props.setColumnScales({ ...props.columnScales(), [key]: scaleStr });
      const formattedCols = props.formattedColumns();
      props.setFormattedColumns(formattedCols.includes(key) ? formattedCols : [...formattedCols, key]);
    }
  };

  const formatDelta = (delta: number): string => {
    if (Number.isNaN(delta)) {return "";}
    const n = Number(delta);
    const prefix = n > 0 ? "+" : "";
    return `${prefix}${n.toFixed(1)}`;
  };

  const deltaClass = (d: number) =>
    d > 0 ? "race-summary-delta race-summary-delta-positive" : d < 0 ? "race-summary-delta race-summary-delta-negative" : "race-summary-delta";

  interface CellContext {
    scales: Record<string, string>;
    formatted: string[];
    /** Reference entry for deltas (first row unpivoted / left-most column pivoted); null disables deltas. */
    deltaReference: Record<string, unknown> | null;
  }

  /** The row-label cell (team name in fleet/day mode) — coloured per source. */
  const renderLabelCell = (
    row: Record<string, unknown>,
    key: string,
    ctx: CellContext
  ): JSX.Element => {
    const labelVal = getRowValue(row, key);
    const isSource = key.toLowerCase() === "source_name";
    const scaleClass = cellClassForScale(row, key, ctx.scales, ctx.formatted);
    return (
      <td
        class={`centered ${scaleClass}${isSource ? ` team-cell${getTeamCellHaloClass(String(labelVal ?? ""))}` : ""}`}
        style={isSource ? getTeamCellStyle(String(labelVal ?? "")) : undefined}
      >
        {labelVal != null ? String(labelVal) : "—"}
      </td>
    );
  };

  /** A metric value cell. Shared by both orientations so formatting/deltas stay identical. */
  const renderValueCell = (
    row: Record<string, unknown>,
    key: string,
    ctx: CellContext
  ): JSX.Element => {
    const scaleClass = cellClassForScale(row, key, ctx.scales, ctx.formatted);
    const lk = key.toLowerCase();
    const val = getRowValue(row, key);
    const firstVal = ctx.deltaReference ? getRowValue(ctx.deltaReference, key) : null;
    const delta =
      ctx.deltaReference != null &&
      lk !== "count" &&
      val != null &&
      firstVal != null &&
      typeof val === "number" &&
      typeof firstVal === "number" &&
      !Number.isNaN(val) &&
      !Number.isNaN(firstVal)
        ? val - firstVal
        : null;
    const displayText = val != null ? (typeof val === "number" ? formatNum(val) : String(val)) : "—";
    return (
      <td class={`centered ${scaleClass}`}>
        {displayText}
        {delta != null && <span class={deltaClass(delta)}>{formatDelta(delta)}</span>}
      </td>
    );
  };

  return (
    <Show
      when={props.rows().length > 0}
      fallback={<p class="cheat-sheet-empty-message">{props.emptyMessage}</p>}
    >
      <div class="scrollable-container">
        {(() => {
          const scales = props.columnScales();
          const formatted = props.formattedColumns();
          const sortedList = sortedRows();
          const firstRow = sortedList.length > 0 ? sortedList[0] : null;
          const deltasEnabled = props.showDeltas();
          const cols = tableColumns();
          const sortMarker = (key: string): string =>
            props.sortConfig().key === key ? (props.sortConfig().direction === "asc" ? "▲" : "▼") : "";

          if (isPivoted()) {
            // Metrics down the left, one column per source — shared with the other report
            // pages that offer the pivot (Race Summary, Training Summary).
            return (
              <PivotedMetricTable
                rows={sortedRows}
                rowLabelKey={props.rowLabelKey}
                metricKeys={pivotMetricKeys}
                getHeader={props.getHeader}
                sortConfig={props.sortConfig}
                onSort={sortData}
                columnScales={props.columnScales}
                formattedColumns={props.formattedColumns}
                showDeltas={props.showDeltas}
                // These tables carry `count` as a number and print it through formatNum, so
                // the shared default (which also covers record_count) matches today exactly.
                noDeltaKeys={["count"]}
              />
            );
          }

          return (
            <table class="maneuvers-table">
              <thead>
                <tr>
                  <For each={cols}>
                    {(key) => (
                      <th
                        class="centered"
                        style={{ cursor: "pointer" }}
                        onClick={() => sortData(key)}
                        title={`Sort by ${props.getHeader(key)}`}
                      >
                        {props.getHeader(key)} {sortMarker(key)}
                      </th>
                    )}
                  </For>
                </tr>
              </thead>
              <tbody>
                <For each={sortedList}>
                  {(row, index) => {
                    const ctx = {
                      scales,
                      formatted,
                      deltaReference: deltasEnabled && index() > 0 ? firstRow : null,
                    };
                    return (
                      <tr>
                        <For each={cols}>
                          {(key) =>
                            isRowLabelKey(key.toLowerCase())
                              ? renderLabelCell(row, key, ctx)
                              : renderValueCell(row, key, ctx)
                          }
                        </For>
                      </tr>
                    );
                  }}
                </For>
              </tbody>
            </table>
          );
        })()}
      </div>
    </Show>
  );
};

export interface DailyTrainingFleetContext {
  startDate: () => string;
  endDate: () => string;
  filters: () => SummaryTablesHistoryFilters;
  sourceNames: () => string[];
  showTimeline: () => boolean;
  /** Live fleet view: restrict queries to LIVE datasets (report_name='LIVE'). Default false. */
  liveOnly?: () => boolean;
  /** Allow the "ALL" TWS option in fleet mode (live view averages across all wind speeds). Default false. */
  allowTwsAll?: () => boolean;
  /** Bumping this value re-runs the timeline + table queries (Refresh button). */
  refreshNonce?: () => number;
  /**
   * Pivot the tables: metrics as rows, sources as columns. Display-only — it must never
   * feed the baseline cache key or the fetch context, or toggling it would refetch.
   * Default false. Lives here (fleet-only bag) so day/dataset modes cannot reach it.
   */
  pivotTables?: () => boolean;
}

export interface DailyTrainingSettingsSectionProps {
  mode: DailyTrainingSettingsView;
  pageLabel?: string;
  /** Page title element; use h2/none when embedded in day Summary Tables. */
  titleLevel?: "h1" | "h2" | "none";
  /** Required when mode is fleet — project history scope and filters. */
  fleetContext?: DailyTrainingFleetContext;
  /** Optional gear/settings control rendered before Point of Sail (fleet Summary Tables). */
  settingsControl?: JSX.Element;
}

function DailyTrainingSettingsTitle(props: { pageLabel?: string; titleLevel?: "h1" | "h2" | "none" }) {
  const level = () => props.titleLevel ?? "h1";
  const label = () => props.pageLabel ?? "Training Settings";
  return (
    <Show when={level() !== "none"}>
      <Show
        when={level() === "h2"}
        fallback={<h1 class="report-page-title cheat-sheet-title">{label()}</h1>}
      >
        <h2 class="cheat-sheet-section-title report-section-heading">{label()}</h2>
      </Show>
    </Show>
  );
}

export default function DailyTrainingSettingsSection(props: DailyTrainingSettingsSectionProps) {
  const [loading, setLoading] = createSignal(true);
  const [tablesLoading, setTablesLoading] = createSignal(false);
  const [maneuversLoading, setManeuversLoading] = createSignal(false);
  const [tablesDataReady, setTablesDataReady] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [straightRows, setStraightRows] = createSignal<Record<string, unknown>[]>([]);
  const [maneuverRows, setManeuverRows] = createSignal<Record<string, unknown>[]>([]);
  const [accelerationRows, setAccelerationRows] = createSignal<Record<string, unknown>[]>([]);
  const [twsBins, setTwsBins] = createSignal<number[]>([]);
  const [scatterPoints, setScatterPoints] = createSignal<DailyTrainingScatterPoint[]>([]);
  const [selectedTwsLabel, setSelectedTwsLabel] = createSignal<string>(
    props.mode === "fleet" && props.fleetContext?.allowTwsAll?.() !== true
      ? String(FLEET_DEFAULT_TWS_BIN)
      : TWS_ALL_LABEL
  );
  const [brushedEventIds, setBrushedEventIds] = createSignal<number[] | null>(null);
  const [brushedTimeRange, setBrushedTimeRange] = createSignal<{ start: Date; end: Date } | null>(null);
  const [legType, setLegType] = createSignal<DailyTrainingLegType>("upwind");
  const [maneuverType, setManeuverType] = createSignal<DailyTrainingManeuverType>("tack");

  const [sortConfigStraight, setSortConfigStraight] = createSignal<{ key: string; direction: "asc" | "desc" }>({
    key: "vmg_percent",
    direction: "desc",
  });
  const [columnScalesStraight, setColumnScalesStraight] = createSignal<Record<string, string>>({});
  const [formattedColumnsStraight, setFormattedColumnsStraight] = createSignal<string[]>([]);
  const [showDeltasStraight, setShowDeltasStraight] = createSignal(true);

  const [sortConfigManeuver, setSortConfigManeuver] = createSignal<{ key: string; direction: "asc" | "desc" }>({
    key: "vmg_perc_avg",
    direction: "desc",
  });
  const [columnScalesManeuver, setColumnScalesManeuver] = createSignal<Record<string, string>>({});
  const [formattedColumnsManeuver, setFormattedColumnsManeuver] = createSignal<string[]>([]);
  const [showDeltasManeuver, setShowDeltasManeuver] = createSignal(true);

  const [sortConfigAcceleration, setSortConfigAcceleration] = createSignal<{ key: string; direction: "asc" | "desc" }>({
    key: "vmg_perc_build",
    direction: "desc",
  });
  const [columnScalesAcceleration, setColumnScalesAcceleration] = createSignal<Record<string, string>>({});
  const [formattedColumnsAcceleration, setFormattedColumnsAcceleration] = createSignal<string[]>([]);
  const [showDeltasAcceleration, setShowDeltasAcceleration] = createSignal(true);

  const [isHoveredStraightTable, setIsHoveredStraightTable] = createSignal(false);
  const [showCopyStraightTable, setShowCopyStraightTable] = createSignal(false);
  const [copySuccessStraightTable, setCopySuccessStraightTable] = createSignal(false);
  const [isHoveredManeuverTable, setIsHoveredManeuverTable] = createSignal(false);
  const [showCopyManeuverTable, setShowCopyManeuverTable] = createSignal(false);
  const [copySuccessManeuverTable, setCopySuccessManeuverTable] = createSignal(false);
  const [isHoveredAccelerationTable, setIsHoveredAccelerationTable] = createSignal(false);
  const [showCopyAccelerationTable, setShowCopyAccelerationTable] = createSignal(false);
  const [copySuccessAccelerationTable, setCopySuccessAccelerationTable] = createSignal(false);

  let timelineFetchController: AbortController | null = null;
  let tablesFetchController: AbortController | null = null;
  let maneuversFetchController: AbortController | null = null;
  let timelineFetchSeq = 0;
  let tablesFetchSeq = 0;
  let maneuversFetchSeq = 0;
  let contextFetchSeq = 0;
  let contextFetchInProgress = false;
  let maneuverOnlyFetchInProgress = false;
  let baselineTableCache: BaselineTableCache | null = null;

  const clearBaselineTableCache = (): void => {
    baselineTableCache = null;
  };

  const baselineCacheKey = createMemo((): string => {
    const base: Record<string, unknown> = {
      mode: props.mode,
      legType: legType(),
      twsLabel: selectedTwsLabel(),
      className: selectedClassName(),
      projectId: selectedProjectId(),
    };
    if (props.mode === "dataset") {
      base.date = dateNorm();
      base.datasetId = selectedDatasetId();
      base.sourceId = selectedSourceId();
    } else if (props.mode === "day") {
      base.date = dateNorm();
    } else if (props.mode === "fleet" && props.fleetContext) {
      base.startDate = props.fleetContext.startDate();
      base.endDate = props.fleetContext.endDate();
      base.filters = props.fleetContext.filters();
      base.sourceNames = props.fleetContext.sourceNames();
    }
    return JSON.stringify(base);
  });

  const storeBaselineTableCache = (
    straight: Record<string, unknown>[],
    maneuver: Record<string, unknown>[],
    acceleration: Record<string, unknown>[],
    bins: number[]
  ): void => {
    const mType = untrack(maneuverType);
    const key = baselineCacheKey();
    baselineTableCache = {
      cacheKey: key,
      straightRows: straight,
      maneuverRowsByType: {
        ...(baselineTableCache?.cacheKey === key ? baselineTableCache.maneuverRowsByType : {}),
        [mType]: maneuver,
      },
      accelerationRows: acceleration,
      twsBins: bins,
    };
  };

  const updateBaselineManeuverCache = (maneuver: Record<string, unknown>[]): void => {
    const key = baselineCacheKey();
    const mType = untrack(maneuverType);
    if (baselineTableCache?.cacheKey !== key) {
      return;
    }
    baselineTableCache = {
      ...baselineTableCache,
      maneuverRowsByType: {
        ...baselineTableCache.maneuverRowsByType,
        [mType]: maneuver,
      },
    };
  };

  /** Restore straight-line rows only (does not read maneuver type — safe for TWS/brush effects). */
  const restoreBaselineStraightFromCache = (): boolean => {
    const key = untrack(baselineCacheKey);
    if (baselineTableCache?.cacheKey !== key) {
      return false;
    }
    setStraightRows(baselineTableCache.straightRows);
    setAccelerationRows(baselineTableCache.accelerationRows);
    if (baselineTableCache.twsBins.length > 0) {
      untrack(() => syncSelectedTwsLabel(baselineTableCache!.twsBins));
    }
    return true;
  };

  const restoreManeuverFromCache = (): boolean => {
    const key = untrack(baselineCacheKey);
    if (baselineTableCache?.cacheKey !== key) {
      return false;
    }
    const mType = maneuverType();
    const cached = baselineTableCache.maneuverRowsByType[mType];
    if (cached == null) {
      return false;
    }
    setManeuverRows(cached);
    return true;
  };

  /** Restore straight + current maneuver type after clearing a timeline brush. */
  const restoreFullBaselineFromCache = (): boolean => {
    if (!restoreBaselineStraightFromCache()) {
      return false;
    }
    restoreManeuverFromCache();
    return true;
  };

  onCleanup(() => {
    timelineFetchController?.abort();
    tablesFetchController?.abort();
    maneuversFetchController?.abort();
  });

  const maneuverColumnOrder = createMemo(() =>
    trainingSummaryManeuverColumnOrderForType(maneuverType())
  );

  const dateNorm = (): string => {
    const d = selectedDate();
    if (!d) {return "";}
    return String(d).replace(/[-/]/g, "");
  };

  const rowLabelKey = (): string =>
    props.mode === "day" || props.mode === "fleet" ? "source_name" : "tws";

  const isSourceGroupedMode = (): boolean => props.mode === "day" || props.mode === "fleet";

  /** Live fleet summary: fleet mode scoped to today's LIVE session (single day → time axis). */
  const isLiveFleet = (): boolean => props.mode === "fleet" && props.fleetContext?.liveOnly?.() === true;

  /** Pivoted tables (metrics as rows, sources as columns) — fleet mode only, display-only. */
  const pivotTables = (): boolean =>
    props.mode === "fleet" && props.fleetContext?.pivotTables?.() === true;

  const getStraightHeader = (key: string): string => {
    const k = key.toLowerCase();
    if (k === "source_name") {return "TEAM";}
    if (k === "tws_avg" || k === "tws") {
      return cheatSheetTwsBinColumnHeader(summaryTablesSpeedUnit());
    }
    return trainingSummaryStraightTableHeader(key, summaryTablesSpeedUnit());
  };

  const getManeuverHeader = (key: string): string => {
    return trainingSummaryManeuverTableHeader(key, summaryTablesSpeedUnit(), maneuverType());
  };

  const getAccelerationHeader = (key: string): string => {
    return trainingSummaryAccelerationTableHeader(key, summaryTablesSpeedUnit());
  };

  const twsDropdownOptions = createMemo((): string[] => {
    if (props.mode === "fleet") {
      const fleetLabels = FLEET_TWS_BINS.map((b) => formatDailySettingsTwsBinDropdownLabel(b));
      return props.fleetContext?.allowTwsAll?.() === true
        ? [TWS_ALL_LABEL, ...fleetLabels]
        : fleetLabels;
    }
    const bins = twsBins();
    const labels = bins.map((b) => formatDailySettingsTwsBinDropdownLabel(b));
    if (bins.length === 0) {return [TWS_ALL_LABEL];}
    return [TWS_ALL_LABEL, ...labels];
  });

  const twsDropdownDefaultText = (): string => {
    const label = selectedTwsLabel();
    if (label) {return label;}
    return props.mode === "fleet" ? String(FLEET_DEFAULT_TWS_BIN) : TWS_ALL_LABEL;
  };

  const showTwsDropdown = (): boolean =>
    twsDropdownOptions().length > (props.mode === "fleet" ? 0 : 1);

  const selectedTwsBin = (): number | null => {
    const label = selectedTwsLabel();
    if (label === TWS_ALL_LABEL || label === "") {return null;}
    return twsBinCenterFromDropdownLabel(label);
  };

  const apiEventIdsParam = createMemo((): number[] | null => {
    if (props.mode === "fleet") {return null;}
    const ids = brushedEventIds();
    return ids != null && ids.length > 0 ? ids : null;
  });

  const apiBrushTimeParams = createMemo((): { brushStartTime: string; brushEndTime: string } | null => {
    const range = brushedTimeRange();
    if (range == null) {return null;}
    return {
      brushStartTime: range.start.toISOString(),
      brushEndTime: range.end.toISOString(),
    };
  });

  const filteredScatterPoints = createMemo((): DailyTrainingScatterPoint[] => {
    let pts = scatterPoints();
    const bin = selectedTwsBin();
    if (bin != null) {
      pts = pts.filter((p) => p.tws_bin === bin);
    }
    return pts;
  });

  const scatterTimelinePoints = createMemo((): Record<string, unknown>[] => {
    const pts = filteredScatterPoints();
    if (props.mode === "fleet") {
      return mapCoveragePointsToTimelinePoints(
        pts.map((p) => ({
          event_id: p.event_id,
          start_time: p.start_time,
          source_name: p.source_name,
          tws_bin: p.tws_bin,
          tws: p.tws,
        })),
        SPEED_UNIT_KPH
      );
    }
    return mapTrainingScatterToTimelinePoints(pts);
  });

  const scatterTimelineGroups = createMemo(() => {
    const pts = scatterTimelinePoints();
    if (pts.length === 0) {return [];}
    const { groups } = buildColorGrouping(pts as Record<string, unknown>[], "SOURCE");
    return groups;
  });

  const scatterTimelineColorScale = createMemo((): ((v: string) => string) | undefined => {
    const pts = scatterTimelinePoints();
    if (pts.length === 0) {return undefined;}
    const { getItemColor } = buildColorGrouping(pts as Record<string, unknown>[], "SOURCE");
    return (normalizedLower: string) =>
      getItemColor({ source_name: normalizedLower } as Record<string, unknown>);
  });

  const hasActiveBrushFilter = (): boolean => {
    const range = brushedTimeRange();
    if (range != null) {
      return true;
    }
    const ids = brushedEventIds();
    return ids != null && ids.length > 0;
  };

  const clearTimelineBrushFilter = (): void => {
    if (!hasActiveBrushFilter()) {
      return;
    }
    setBrushedEventIds(null);
    setBrushedTimeRange(null);
    if (restoreFullBaselineFromCache()) {
      return;
    }
    if (!untrack(loading) && !contextFetchInProgress && !maneuverOnlyFetchInProgress) {
      void fetchBaselineTables();
    }
  };

  const handleTimelineBrush = (detail: { eventIds: number[]; startTime: Date | null; endTime: Date | null }) => {
    const hasTimeRange = detail.startTime != null && detail.endTime != null;

    if (!hasTimeRange && detail.eventIds.length === 0) {
      setBrushedEventIds(null);
      setBrushedTimeRange(null);
      restoreFullBaselineFromCache();
      return;
    }

    if (!untrack(loading) && !contextFetchInProgress) {
      setTablesLoading(true);
    }

    if (props.mode === "fleet") {
      setBrushedEventIds(null);
      if (hasTimeRange) {
        setBrushedTimeRange({ start: detail.startTime!, end: detail.endTime! });
      } else {
        setBrushedTimeRange(null);
      }
      return;
    }

    if (detail.eventIds.length === 0) {
      setBrushedEventIds(null);
      setBrushedTimeRange(null);
      restoreFullBaselineFromCache();
      return;
    }

    setBrushedEventIds(detail.eventIds);
    if (hasTimeRange) {
      setBrushedTimeRange({ start: detail.startTime!, end: detail.endTime! });
    } else {
      setBrushedTimeRange(null);
    }
  };

  onMount(() => {
    const onSelectionBannerCleared = () => {
      clearTimelineBrushFilter();
    };
    window.addEventListener("selection-banner-cleared", onSelectionBannerCleared);
    onCleanup(() => {
      window.removeEventListener("selection-banner-cleared", onSelectionBannerCleared);
    });
  });

  type FetchParams = {
    className: string;
    projectId: string;
    dateYmd?: string;
    speedUnit: "kts" | "kph";
  };

  const resolveFetchParams = (): FetchParams | null => {
    const className = selectedClassName();
    const projectId = selectedProjectId();
    if (!className || !projectId) {
      return null;
    }
    const speedUnit = "kph" as const;
    if (props.mode === "fleet") {
      if (!props.fleetContext) {
        return null;
      }
      const start = props.fleetContext.startDate();
      const end = props.fleetContext.endDate();
      if (!fleetDateRangeReady(start, end)) {
        return null;
      }
      return { className, projectId, speedUnit };
    }
    const date = dateNorm();
    if (date.length !== 8) {
      return null;
    }
    return { className, projectId, dateYmd: date, speedUnit };
  };

  const sharedSettingsParams = (base: FetchParams) => ({
    className: base.className,
    projectId: base.projectId,
    dateYmd: base.dateYmd,
    view: props.mode,
    legType: legType(),
    maneuverType: maneuverType(),
    speedUnit: base.speedUnit,
    datasetId: props.mode === "dataset" ? Number(selectedDatasetId()) || null : null,
    sourceId: props.mode === "dataset" ? Number(selectedSourceId()) || null : null,
    startDate: props.mode === "fleet" ? props.fleetContext?.startDate() ?? "" : undefined,
    endDate: props.mode === "fleet" ? props.fleetContext?.endDate() ?? "" : undefined,
    filters: props.mode === "fleet" ? props.fleetContext?.filters() ?? {} : undefined,
    sourceNames: props.mode === "fleet" ? props.fleetContext?.sourceNames() ?? [] : undefined,
    liveOnly: props.mode === "fleet" ? (props.fleetContext?.liveOnly?.() ?? false) : undefined,
  });

  const tableTwsParams = () => {
    const bin = selectedTwsBin();
    return {
      twsBin: isSourceGroupedMode() && bin != null ? bin : null,
      twsBins: props.mode === "dataset" && bin != null ? [bin] : null,
    };
  };

  /** API already filters by tws_bin for fleet/day when a bin is selected. */
  const apiFiltersTwsByBin = createMemo(() => {
    const bin = selectedTwsBin();
    return isSourceGroupedMode() && bin != null;
  });

  const coveragePointsToScatter = (points: DailyTrainingCoveragePoint[]): DailyTrainingScatterPoint[] =>
    points.map((p) => ({
      event_id: p.event_id,
      start_time: p.start_time,
      source_name: p.source_name,
      tws_bin: p.tws_bin,
      tws: p.tws,
    }));

  const syncSelectedTwsLabel = (available: number[]) => {
    const fleetBins = [...FLEET_TWS_BINS];
    const allowedBins = props.mode === "fleet" ? fleetBins : available;
    if (allowedBins.length === 0) {return;}
    const label = selectedTwsLabel();
    const disallowAll = props.mode === "fleet" && props.fleetContext?.allowTwsAll?.() !== true;

    if (disallowAll && (label === TWS_ALL_LABEL || label === "")) {
      setSelectedTwsLabel(fleetDefaultTwsLabel(allowedBins));
      return;
    }

    if (label !== TWS_ALL_LABEL) {
      const current = twsBinCenterFromDropdownLabel(label);
      if (current == null || !allowedBins.includes(current)) {
        setSelectedTwsLabel(disallowAll ? fleetDefaultTwsLabel(allowedBins) : TWS_ALL_LABEL);
      }
    }
  };

  /** Fleet mode: sparse daily coverage markers (coverage-timeline endpoint). */
  const fetchCoverageTimeline = async (signal: AbortSignal, requestSeq: number): Promise<boolean> => {
    const base = resolveFetchParams();
    if (!base || props.mode !== "fleet") {
      if (requestSeq === timelineFetchSeq) {
        setScatterPoints([]);
        setTwsBins([]);
      }
      return false;
    }

    const result = await fetchDailyTrainingCoverageTimeline({
      ...sharedSettingsParams(base),
      twsBin: null,
      signal,
    });

    if (requestSeq !== timelineFetchSeq || signal.aborted) {
      return false;
    }
    if (result.message === "Request cancelled") {
      return false;
    }

    if (!result.ok) {
      setError(result.message);
      setScatterPoints([]);
      setTwsBins([]);
      return false;
    }

    setError(null);
    setScatterPoints(coveragePointsToScatter(result.data.coverage_points));
    setTwsBins(result.data.tws_bins);
    syncSelectedTwsLabel(result.data.tws_bins);
    return true;
  };

  /** Timeline scatter + bin list: never filtered by brush; TWS applied client-side only. */
  const fetchTimeline = async (): Promise<boolean> => {
    timelineFetchController?.abort();
    timelineFetchController = new AbortController();
    const signal = timelineFetchController.signal;
    const requestSeq = ++timelineFetchSeq;

    if (props.mode === "fleet") {
      if (props.fleetContext?.showTimeline() !== true) {
        if (requestSeq === timelineFetchSeq) {
          setScatterPoints([]);
        }
        return true;
      }
      return fetchCoverageTimeline(signal, requestSeq);
    }

    const base = resolveFetchParams();
    if (!base) {
      if (requestSeq === timelineFetchSeq) {
        setScatterPoints([]);
        setTwsBins([]);
        setError(null);
      }
      return false;
    }

    const result = await fetchDailyTrainingSettings({
      ...sharedSettingsParams(base),
      twsBin: null,
      twsBins: null,
      eventIds: null,
      signal,
    });

    if (requestSeq !== timelineFetchSeq || signal.aborted) {
      return false;
    }
    if (result.message === "Request cancelled") {
      return false;
    }

    if (!result.ok) {
      setError(result.message);
      setScatterPoints([]);
      setTwsBins([]);
      return false;
    }

    setError(null);
    setScatterPoints(result.data.scatter_points);
    setTwsBins(result.data.tws_bins);
    syncSelectedTwsLabel(result.data.tws_bins);
    return true;
  };

  /** Tables: unbrushed payload for current PoS/TWS/filters — stored in baseline cache. */
  const fetchBaselineTables = async (opts?: { fromContext?: boolean }): Promise<void> => {
    const fromContext = opts?.fromContext === true;
    if (!fromContext) {
      setTablesLoading(true);
    }

    if (!maneuverOnlyFetchInProgress) {
      maneuversFetchController?.abort();
    }
    tablesFetchController?.abort();
    tablesFetchController = new AbortController();
    const signal = tablesFetchController.signal;
    const requestSeq = ++tablesFetchSeq;

    try {
      const base = resolveFetchParams();
      if (!base) {
        if (requestSeq === tablesFetchSeq) {
          setStraightRows([]);
          setManeuverRows([]);
          setAccelerationRows([]);
          clearBaselineTableCache();
        }
        return;
      }

      const result = await fetchDailyTrainingSettings({
        ...sharedSettingsParams(base),
        ...tableTwsParams(),
        eventIds: null,
        brushStartTime: null,
        brushEndTime: null,
        signal,
      });

      if (requestSeq !== tablesFetchSeq || signal.aborted) {
        return;
      }
      if (result.message === "Request cancelled") {
        return;
      }

      if (!result.ok) {
        setError(result.message);
        setStraightRows([]);
        setManeuverRows([]);
        setAccelerationRows([]);
        clearBaselineTableCache();
        return;
      }

      setError(null);
      setStraightRows(result.data.straight_line_rows);
      setManeuverRows(result.data.maneuver_rows);
      setAccelerationRows(result.data.acceleration_rows);
      storeBaselineTableCache(
        result.data.straight_line_rows,
        result.data.maneuver_rows,
        result.data.acceleration_rows,
        result.data.tws_bins
      );
      syncSelectedTwsLabel(result.data.tws_bins);
    } finally {
      if (!fromContext && requestSeq === tablesFetchSeq) {
        setTablesLoading(false);
      }
      if (fromContext && requestSeq === tablesFetchSeq) {
        setTablesDataReady(true);
      }
    }
  };

  /** Tables: narrower time range from timeline brush — does not overwrite baseline cache. */
  const fetchBrushedTables = async (): Promise<void> => {
    setTablesLoading(true);

    if (!maneuverOnlyFetchInProgress) {
      maneuversFetchController?.abort();
    }
    tablesFetchController?.abort();
    tablesFetchController = new AbortController();
    const signal = tablesFetchController.signal;
    const requestSeq = ++tablesFetchSeq;

    try {
      const base = resolveFetchParams();
      if (!base) {
        if (requestSeq === tablesFetchSeq) {
          setStraightRows([]);
          setManeuverRows([]);
          setAccelerationRows([]);
        }
        return;
      }

      const brushTimes = apiBrushTimeParams();
      const result = await fetchDailyTrainingSettings({
        ...sharedSettingsParams(base),
        ...tableTwsParams(),
        eventIds: apiEventIdsParam(),
        brushStartTime: brushTimes?.brushStartTime ?? null,
        brushEndTime: brushTimes?.brushEndTime ?? null,
        signal,
      });

      if (requestSeq !== tablesFetchSeq || signal.aborted) {
        return;
      }
      if (result.message === "Request cancelled") {
        return;
      }

      if (!result.ok) {
        setError(result.message);
        setStraightRows([]);
        setManeuverRows([]);
        setAccelerationRows([]);
        return;
      }

      setError(null);
      setStraightRows(result.data.straight_line_rows);
      setManeuverRows(result.data.maneuver_rows);
      setAccelerationRows(result.data.acceleration_rows);
    } finally {
      if (requestSeq === tablesFetchSeq) {
        setTablesLoading(false);
      }
    }
  };

  /** Maneuver table only — used when maneuver type changes (no straight-line / timeline reload). */
  const fetchManeuvers = async (): Promise<void> => {
    maneuverOnlyFetchInProgress = true;
    setManeuversLoading(true);

    maneuversFetchController?.abort();
    maneuversFetchController = new AbortController();
    const signal = maneuversFetchController.signal;
    const requestSeq = ++maneuversFetchSeq;

    try {
      const base = resolveFetchParams();
      if (!base) {
        if (requestSeq === maneuversFetchSeq) {
          setManeuverRows([]);
        }
        return;
      }

      const brushTimes = apiBrushTimeParams();
      const result = await fetchDailyTrainingSettings({
        ...sharedSettingsParams(base),
        ...tableTwsParams(),
        eventIds: apiEventIdsParam(),
        brushStartTime: brushTimes?.brushStartTime ?? null,
        brushEndTime: brushTimes?.brushEndTime ?? null,
        sections: "maneuvers",
        signal,
      });

      if (requestSeq !== maneuversFetchSeq || signal.aborted) {
        return;
      }
      if (result.message === "Request cancelled") {
        return;
      }

      if (!result.ok) {
        setError(result.message);
        setManeuverRows([]);
        return;
      }

      setError(null);
      setManeuverRows(result.data.maneuver_rows);
      if (!hasActiveBrushFilter()) {
        updateBaselineManeuverCache(result.data.maneuver_rows);
      }
    } finally {
      if (requestSeq === maneuversFetchSeq) {
        maneuverOnlyFetchInProgress = false;
        setManeuversLoading(false);
      }
    }
  };

  const changeManeuverType = (next: DailyTrainingManeuverType): void => {
    if (next === maneuverType()) {
      return;
    }
    setManeuverType(next);
    clearFormattingManeuver();
    if (restoreManeuverFromCache()) {
      return;
    }
    void fetchManeuvers();
  };

  const fetchContext = async () => {
    const requestSeq = ++contextFetchSeq;
    const base = resolveFetchParams();
    if (!base) {
      if (props.mode === "fleet" && selectedClassName() && selectedProjectId()) {
        if (requestSeq === contextFetchSeq) {
          contextFetchInProgress = true;
          setLoading(true);
          setTablesDataReady(false);
        }
        return;
      }
      if (requestSeq !== contextFetchSeq) {
        return;
      }
      setStraightRows([]);
      setManeuverRows([]);
      setTwsBins([]);
      setScatterPoints([]);
      setError(null);
      setLoading(false);
      setTablesDataReady(true);
      return;
    }

    if (requestSeq !== contextFetchSeq) {
      return;
    }

    contextFetchInProgress = true;
    setLoading(true);
    setTablesDataReady(false);
    setError(null);
    setBrushedEventIds(null);
    setBrushedTimeRange(null);
    clearBaselineTableCache();
    try {
      const timelineOk = await fetchTimeline();
      if (requestSeq !== contextFetchSeq) {
        return;
      }
      if (timelineOk) {
        await fetchBaselineTables({ fromContext: true });
      }
    } finally {
      if (requestSeq === contextFetchSeq) {
        contextFetchInProgress = false;
        setLoading(false);
        setTablesLoading(false);
        setManeuversLoading(false);
        if (!tablesDataReady()) {
          setTablesDataReady(true);
        }
      }
    }
  };

  const refetchTablesForFilters = (): void => {
    if (untrack(loading) || contextFetchInProgress || maneuverOnlyFetchInProgress) {
      return;
    }
    if (hasActiveBrushFilter()) {
      void fetchBrushedTables();
      return;
    }
    if (restoreBaselineStraightFromCache()) {
      return;
    }
    void fetchBaselineTables();
  };

  createEffect(
    on(
      () => {
        const ctx: Record<string, unknown> = {
          mode: props.mode,
          className: selectedClassName(),
          projectId: selectedProjectId(),
          legType: legType(),
        };
        if (props.mode === "dataset") {
          ctx.date = dateNorm();
          ctx.datasetId = selectedDatasetId();
          ctx.sourceId = selectedSourceId();
        } else if (props.mode === "day") {
          ctx.date = dateNorm();
        } else if (props.mode === "fleet" && props.fleetContext) {
          ctx.startDate = props.fleetContext.startDate();
          ctx.endDate = props.fleetContext.endDate();
          ctx.filters = props.fleetContext.filters();
          ctx.sourceNames = props.fleetContext.sourceNames();
          ctx.showTimeline = props.fleetContext.showTimeline();
          ctx.liveOnly = props.fleetContext.liveOnly?.() ?? false;
          ctx.refreshNonce = props.fleetContext.refreshNonce?.() ?? 0;
        }
        return JSON.stringify(ctx);
      },
      () => {
        void fetchContext();
      }
    )
  );

  createEffect(
    on(
      () =>
        [
          selectedTwsLabel(),
          brushedEventIds(),
          brushedTimeRange(),
        ] as const,
      () => {
        refetchTablesForFilters();
      },
      { defer: true }
    )
  );

  const straightRowsForTable = createMemo(() => {
    const rows = straightRows();
    if (apiFiltersTwsByBin()) {
      return dedupeDailyTrainingRowsBySource(rows);
    }
    const bin = selectedTwsBin();
    if (isSourceGroupedMode()) {
      return bin != null ? filterDailyTrainingRowsForDayView(rows, bin) : rows;
    }
    return filterDailyTrainingRowsForDatasetView(rows, bin != null ? [bin] : null);
  });

  const maneuverRowsForTable = createMemo(() => {
    const rows = maneuverRows();
    if (apiFiltersTwsByBin()) {
      return dedupeDailyTrainingRowsBySource(rows);
    }
    const bin = selectedTwsBin();
    if (isSourceGroupedMode()) {
      return bin != null ? filterDailyTrainingRowsForDayView(rows, bin) : rows;
    }
    return filterDailyTrainingRowsForDatasetView(rows, bin != null ? [bin] : null);
  });

  const accelerationRowsForTable = createMemo(() => {
    const rows = accelerationRows();
    if (apiFiltersTwsByBin()) {
      return dedupeDailyTrainingRowsBySource(rows);
    }
    const bin = selectedTwsBin();
    if (isSourceGroupedMode()) {
      return bin != null ? filterDailyTrainingRowsForDayView(rows, bin) : rows;
    }
    return filterDailyTrainingRowsForDatasetView(rows, bin != null ? [bin] : null);
  });

  const straightValueKeys = createMemo(() => {
    if (straightRowsForTable().length === 0) {return [];}
    const label = rowLabelKey().toLowerCase();
    return trainingSummaryStraightColumnOrder.filter((k) => k !== label && k !== "count");
  });

  const maneuverValueKeys = createMemo(() =>
    valueKeysFromRows(maneuverRowsForTable(), rowLabelKey(), isSourceGroupedMode())
  );

  const accelerationValueKeys = createMemo(() =>
    valueKeysFromRows(accelerationRowsForTable(), rowLabelKey(), isSourceGroupedMode())
  );

  const clearFormattingStraight = () => {
    setFormattedColumnsStraight([]);
    setColumnScalesStraight({});
    setSortConfigStraight({ key: "vmg_percent", direction: "desc" });
  };

  const clearFormattingManeuver = () => {
    setFormattedColumnsManeuver([]);
    setColumnScalesManeuver({});
    setSortConfigManeuver({ key: "vmg_perc_avg", direction: "desc" });
  };

  const addAllFormattingStraight = () => {
    const list = straightRowsForTable();
    const vk = straightValueKeys();
    if (list.length === 0 || vk.length === 0) {return;}
    setColumnScalesStraight(recomputeScalesFromData(list, vk, vk));
    setFormattedColumnsStraight([...vk]);
  };

  const addAllFormattingManeuver = () => {
    const list = maneuverRowsForTable();
    const vk = maneuverValueKeys();
    if (list.length === 0 || vk.length === 0) {return;}
    setColumnScalesManeuver(recomputeScalesFromData(list, vk, vk));
    setFormattedColumnsManeuver([...vk]);
  };

  const toggleFormattingStraight = () => {
    if (formattedColumnsStraight().length === 0) {addAllFormattingStraight();}
    else {clearFormattingStraight();}
  };

  const toggleFormattingManeuver = () => {
    if (formattedColumnsManeuver().length === 0) {addAllFormattingManeuver();}
    else {clearFormattingManeuver();}
  };

  const clearFormattingAcceleration = () => {
    setFormattedColumnsAcceleration([]);
    setColumnScalesAcceleration({});
    setSortConfigAcceleration({ key: "vmg_perc_build", direction: "desc" });
  };

  const addAllFormattingAcceleration = () => {
    const list = accelerationRowsForTable();
    const vk = accelerationValueKeys();
    if (list.length === 0 || vk.length === 0) {return;}
    setColumnScalesAcceleration(recomputeScalesFromData(list, vk, vk));
    setFormattedColumnsAcceleration([...vk]);
  };

  const toggleFormattingAcceleration = () => {
    if (formattedColumnsAcceleration().length === 0) {addAllFormattingAcceleration();}
    else {clearFormattingAcceleration();}
  };

  createEffect(() => {
    const rowsList = straightRowsForTable();
    const formatted = formattedColumnsStraight();
    const vk = straightValueKeys();
    if (formatted.length === 0 || rowsList.length === 0) {return;}
    const newScales = recomputeScalesFromData(rowsList, formatted, vk);
    if (Object.keys(newScales).length > 0) {setColumnScalesStraight(newScales);}
  });

  createEffect(() => {
    const rowsList = maneuverRowsForTable();
    const formatted = formattedColumnsManeuver();
    const vk = maneuverValueKeys();
    if (formatted.length === 0 || rowsList.length === 0) {return;}
    const newScales = recomputeScalesFromData(rowsList, formatted, vk);
    if (Object.keys(newScales).length > 0) {setColumnScalesManeuver(newScales);}
  });

  createEffect(() => {
    const rowsList = accelerationRowsForTable();
    const formatted = formattedColumnsAcceleration();
    const vk = accelerationValueKeys();
    if (formatted.length === 0 || rowsList.length === 0) {return;}
    const newScales = recomputeScalesFromData(rowsList, formatted, vk);
    if (Object.keys(newScales).length > 0) {setColumnScalesAcceleration(newScales);}
  });

  createEffect(() => {
    if (props.mode !== "fleet") {
      return;
    }
    if (isHoveredStraightTable()) {
      const timer = setTimeout(() => setShowCopyStraightTable(true), FLEET_TABLE_EXPORT_HOVER_MS);
      onCleanup(() => clearTimeout(timer));
    } else {
      setShowCopyStraightTable(false);
    }
  });

  createEffect(() => {
    if (props.mode !== "fleet") {
      return;
    }
    if (isHoveredManeuverTable()) {
      const timer = setTimeout(() => setShowCopyManeuverTable(true), FLEET_TABLE_EXPORT_HOVER_MS);
      onCleanup(() => clearTimeout(timer));
    } else {
      setShowCopyManeuverTable(false);
    }
  });

  createEffect(() => {
    if (props.mode !== "fleet") {
      return;
    }
    if (isHoveredAccelerationTable()) {
      const timer = setTimeout(() => setShowCopyAccelerationTable(true), FLEET_TABLE_EXPORT_HOVER_MS);
      onCleanup(() => clearTimeout(timer));
    } else {
      setShowCopyAccelerationTable(false);
    }
  });

  const copyStraightTableToClipboard = async (): Promise<void> => {
    try {
      await copySettingsTableToClipboard(
        straightRowsForTable(),
        rowLabelKey(),
        trainingSummaryStraightColumnOrder,
        getStraightHeader,
        sortConfigStraight(),
        { hideTwsColumn: false, strictColumnOrder: true, includeCount: false, pivoted: pivotTables() }
      );
      setCopySuccessStraightTable(true);
      setTimeout(() => setCopySuccessStraightTable(false), 2000);
    } catch (err) {
      logError("DailyTrainingSettingsSection: copy straight-line table failed", err);
    }
  };

  const copyManeuverTableToClipboard = async (): Promise<void> => {
    try {
      await copySettingsTableToClipboard(
        maneuverRowsForTable(),
        rowLabelKey(),
        maneuverColumnOrder(),
        getManeuverHeader,
        sortConfigManeuver(),
        { hideTwsColumn: isSourceGroupedMode(), strictColumnOrder: true, pivoted: pivotTables() }
      );
      setCopySuccessManeuverTable(true);
      setTimeout(() => setCopySuccessManeuverTable(false), 2000);
    } catch (err) {
      logError("DailyTrainingSettingsSection: copy maneuver table failed", err);
    }
  };

  const copyAccelerationTableToClipboard = async (): Promise<void> => {
    try {
      await copySettingsTableToClipboard(
        accelerationRowsForTable(),
        rowLabelKey(),
        trainingSummaryAccelerationColumnOrder,
        getAccelerationHeader,
        sortConfigAcceleration(),
        { hideTwsColumn: isSourceGroupedMode(), strictColumnOrder: true, pivoted: pivotTables() }
      );
      setCopySuccessAccelerationTable(true);
      setTimeout(() => setCopySuccessAccelerationTable(false), 2000);
    } catch (err) {
      logError("DailyTrainingSettingsSection: copy acceleration table failed", err);
    }
  };

  const legDisplay = (): string => {
    const leg = legType();
    return leg === "reaching" ? "REACH" : leg === "upwind" ? "UPWIND" : "DOWNWIND";
  };

  const maneuverDisplay = (): string => maneuverType().toUpperCase();

  /** Fleet Summary follows the cheat sheet: point of sail is upwind/downwind only (no reach). */
  const pointOfSailOptions = (): string[] =>
    props.mode === "fleet" ? ["UPWIND", "DOWNWIND"] : ["REACH", "UPWIND", "DOWNWIND"];

  /** Point-of-sail change handler; fleet also derives the maneuver type (upwind=tack, downwind=gybe). */
  const applyLegSelection = async (item: string): Promise<void> => {
    const leg: DailyTrainingLegType =
      item === "REACH" ? "reaching" : item === "UPWIND" ? "upwind" : "downwind";
    // Set the derived maneuver type before legType: the refetch effect tracks legType only,
    // so maneuverType must already be current when that fetch reads it (upwind=tack, downwind=gybe).
    if (props.mode === "fleet") {
      setManeuverType(leg === "downwind" ? "gybe" : "tack");
    }
    setLegType(leg);
    const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
    await logActivity(
      project_id,
      dataset_id,
      "DailyTrainingSettingsSection.tsx",
      props.pageLabel ?? "Training Settings",
      `Point of sail changed to ${item}`
    );
  };

  const selectedTwsHint = createMemo(() => {
    const bin = selectedTwsBin();
    if (bin == null) {return "";}
    return ` Selected TWS bin ${bin}: ${formatDailySettingsTwsBinRangeHint(bin, summaryTablesSpeedUnit())}.`;
  });

  const brushHint = createMemo(() => {
    const range = brushedTimeRange();
    if (range != null) {
      return ` Timeline brush: ${formatBrushDateRange(range.start, range.end)}.`;
    }
    if (props.mode === "fleet") {
      return "";
    }
    const brush = brushedEventIds();
    if (brush == null || brush.length === 0) {return "";}
    return ` Timeline brush: ${brush.length} BIN 10 segment(s).`;
  });

  const appliedFiltersHint = createMemo(() => {
    if (props.mode !== "fleet" || !props.fleetContext) {
      return "";
    }
    return formatSummaryTablesAppliedFiltersHint({
      startDate: props.fleetContext.startDate(),
      endDate: props.fleetContext.endDate(),
      sourceNames: props.fleetContext.sourceNames(),
      totalSourceCount: sourcesStore.sources().length,
      filters: props.fleetContext.filters(),
      legType: legType(),
      brushedTimeRange: brushedTimeRange(),
      twsBin: selectedTwsBin(),
      twsDisplayUnit: summaryTablesSpeedUnit(),
    });
  });

  const straightFleetFilterSentence = createMemo((): string => {
    if (props.mode !== "fleet" || !props.fleetContext) {
      return "";
    }
    const parts = [
      "Datasets: visible only",
      "Event type: BIN 10",
      "Aggregate type: AVG rows from events_aggregate",
      ...summaryTablesAppliedFilterParts({
        startDate: props.fleetContext.startDate(),
        endDate: props.fleetContext.endDate(),
        sourceNames: props.fleetContext.sourceNames(),
        totalSourceCount: sourcesStore.sources().length,
        filters: props.fleetContext.filters(),
        legType: legType(),
        brushedTimeRange: brushedTimeRange(),
        twsBin: selectedTwsBin(),
        twsDisplayUnit: summaryTablesSpeedUnit(),
      }),
    ];
    return parts.join("; ");
  });

  const appliedManeuverFiltersHint = createMemo(() => {
    if (props.mode !== "fleet" || !props.fleetContext) {
      return "";
    }
    return formatSummaryTablesAppliedFiltersHint({
      startDate: props.fleetContext.startDate(),
      endDate: props.fleetContext.endDate(),
      sourceNames: props.fleetContext.sourceNames(),
      totalSourceCount: sourcesStore.sources().length,
      filters: props.fleetContext.filters(),
      legType: legType(),
      maneuverType: maneuverType(),
      brushedTimeRange: brushedTimeRange(),
      twsBin: selectedTwsBin(),
      twsDisplayUnit: summaryTablesSpeedUnit(),
    });
  });

  const straightExplanation = createMemo(() => {
    const filterNote = dailyTrainingStraightLineFilterExplanation(legType());
    if (props.mode === "fleet") {
      return `${filterNote}${appliedFiltersHint()}`;
    }
    const scope =
      props.mode === "day"
        ? selectedTwsBin() != null
          ? "one row per boat at the selected TWS bin"
          : "one row per boat across all TWS bins"
        : "one row per TWS bin for this day";
    const dayFilterNote =
      props.mode === "day"
        ? selectedTwsBin() != null
          ? "Columns show the mean of each metric across all BIN 10 straight-line rows in the selected TWS bin."
          : "Columns show the mean of each metric across all BIN 10 straight-line rows for the day."
        : filterNote;
    const layoutHint =
      props.mode === "dataset" ? cheatSheetTwsByConfigTableHint(summaryTablesSpeedUnit()) : "";
    return `${dayFilterNote} (${scope}).${selectedTwsHint()}${brushHint()}${layoutHint}`;
  });

  const maneuverExplanation = createMemo(() => {
    const base = dailyTrainingManeuverFilterExplanation();
    return `${base}${props.mode === "fleet" ? appliedManeuverFiltersHint() : `${selectedTwsHint()}${brushHint()}`}`;
  });

  /** Point-of-sail driven maneuver label: upwind = tacks, downwind = gybes. */
  const legManeuverLabel = (): string => (legType() === "downwind" ? "Gybes" : "Tacks");

  /** Fleet maneuver/acceleration sections name the derived maneuver; other modes keep "Maneuvers". */
  const maneuverSectionTitle = (): string =>
    props.mode === "fleet" ? `Maneuvers - ${legManeuverLabel()}` : "Maneuvers";

  /** Fleet Summary uses the cheat sheet's section-title styling; day/dataset keep the smaller style. */
  const sectionTitleClass = (): string =>
    props.mode === "fleet" ? "report-page-title cheat-sheet-title" : "cheat-sheet-section-title";

  const accelerationSectionTitle = (): string => `Acceleration - ${legManeuverLabel()}`;

  const showAccelerationSection = (): boolean =>
    props.mode === "fleet" && legType() !== "reaching";

  const accelerationExplanation = createMemo(() => {
    const base =
      `Values at maximum build acceleration on exit from ${legManeuverLabel().toLowerCase()} ` +
      `(upwind = tacks, downwind = gybes); one row per boat, mean across the selected maneuvers.`;
    return `${base}${appliedManeuverFiltersHint()}`;
  });

  const straightTableLoading = () => loading() || tablesLoading() || !tablesDataReady();
  const maneuverTableLoading = () => loading() || tablesLoading() || maneuversLoading() || !tablesDataReady();
  const accelerationTableLoading = () => loading() || tablesLoading() || !tablesDataReady();
  const tableDataLoading = () => straightTableLoading() || maneuversLoading();
  const showControls = () => !loading() && !error();
  const showContent = () => !error();

  return (
    <>
      <DailyTrainingSettingsTitle pageLabel={props.pageLabel} titleLevel={props.titleLevel} />

      <Show when={error() && !tableDataLoading()}>
        <div class="cheat-sheet-error">{error()}</div>
      </Show>

      <Show when={props.settingsControl}>
        <div class="race-summary-dropdown-section cheat-sheet-dropdowns training-summary-controls-row">
          <Show when={showControls()}>
            <div class="training-summary-inline-dropdowns">
              <DropDownButton
                options={pointOfSailOptions()}
                defaultText={legDisplay()}
                smallLabel="Point of Sail"
                size="big"
                handleSelection={(item) => void applyLegSelection(item)}
              />
              <Show when={showTwsDropdown()}>
                <DropDownButton
                  options={twsDropdownOptions()}
                  defaultText={twsDropdownDefaultText()}
                  smallLabel="TWS"
                  size="small"
                  handleSelection={async (item) => {
                    setSelectedTwsLabel(item);
                    setBrushedEventIds(null);
                    setBrushedTimeRange(null);
                    const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
                    await logActivity(
                      project_id,
                      dataset_id,
                      "DailyTrainingSettingsSection.tsx",
                      props.pageLabel ?? "Training Settings",
                      `TWS filter changed to ${item}`
                    );
                  }}
                />
              </Show>
            </div>
          </Show>
          {props.settingsControl}
        </div>
      </Show>

      <Show when={!props.settingsControl && showControls()}>
        <div class="race-summary-dropdown-section cheat-sheet-dropdowns">
          <DropDownButton
            options={pointOfSailOptions()}
            defaultText={legDisplay()}
            smallLabel="Point of Sail"
            size="big"
            handleSelection={(item) => void applyLegSelection(item)}
          />
          <Show when={showTwsDropdown()}>
            <DropDownButton
              options={twsDropdownOptions()}
              defaultText={twsDropdownDefaultText()}
              smallLabel="TWS"
              size="small"
              handleSelection={async (item) => {
                setSelectedTwsLabel(item);
                setBrushedEventIds(null);
                setBrushedTimeRange(null);
                const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
                await logActivity(
                  project_id,
                  dataset_id,
                  "DailyTrainingSettingsSection.tsx",
                  props.pageLabel ?? "Training Settings",
                  `TWS filter changed to ${item}`
                );
              }}
            />
          </Show>
        </div>
      </Show>

      <Show when={showContent()}>
        <Show when={tablesLoading() && !loading()}>
          <p class="training-summary-tables-status" aria-live="polite">
            Updating tables…
          </p>
        </Show>
        <Show when={maneuversLoading() && !loading() && !tablesLoading()}>
          <p class="training-summary-tables-status" aria-live="polite">
            Updating maneuver table…
          </p>
        </Show>

        <Show when={!loading()}>
          <Show when={props.mode !== "fleet" || props.fleetContext?.showTimeline() !== false}>
            <Show when={scatterTimelinePoints().length > 0}>
              <section class="training-settings-timeline-section">
                <p class="cheat-sheet-table-explanation">
                  {props.mode === "fleet"
                    ? isLiveFleet()
                      ? "Markers show today's live coverage per boat and TWS bin (average TWS on the vertical axis). Brush a time range to filter the tables below."
                      : "Markers show daily coverage per boat and TWS bin (average TWS on the vertical axis). Brush a date range to filter the tables below."
                    : "Brush a time range on the scatter strip to filter the tables below. The timeline always shows all BIN 10 segments for the selected TWS (or all bins when TWS is ALL)."}
                </p>
                <div class="training-settings-bin-timeline maneuver-selection-timeline">
                  <ScatterTimeseries
                    aggregates={scatterTimelinePoints()}
                    color="SOURCE"
                    groups={scatterTimelineGroups()}
                    colorScale={scatterTimelineColorScale()}
                    skipSelectedSourcesFilter={true}
                    isHistoryPage={props.mode === "fleet" && !isLiveFleet()}
                    onBrushSelect={handleTimelineBrush}
                  />
                </div>
              </section>
            </Show>
          </Show>
        </Show>

        <section class="cheat-sheet-straight-line-section">
          <h2 class={sectionTitleClass()}>Straight line</h2>
          <Show
            when={props.mode === "fleet"}
            fallback={<p class="cheat-sheet-table-explanation">{straightExplanation()}</p>}
          >
            <div class="cheat-sheet-table-explanation cheat-sheet-table-filter-explanation">
              <p class="cheat-sheet-table-filter-intro">
                The data in the tables below represents Bin 10 aggregates with the following filters: {straightFleetFilterSentence()}.
              </p>
            </div>
          </Show>
          <div
            class={props.mode === "fleet" ? "copy-table-hover-wrapper" : undefined}
            onMouseEnter={() => {
              if (props.mode === "fleet") {
                setIsHoveredStraightTable(true);
              }
            }}
            onMouseLeave={() => {
              if (props.mode === "fleet") {
                setIsHoveredStraightTable(false);
              }
            }}
          >
            <div class="data-table-container">
              <Show when={!straightTableLoading()} fallback={<TableLoadingPlaceholder />}>
                <SettingsTable
                  rows={straightRowsForTable}
                  rowLabelKey={rowLabelKey()}
                  columnOrder={trainingSummaryStraightColumnOrder}
                  strictColumnOrder={true}
                  includeCount={false}
                  getHeader={getStraightHeader}
                  sortConfig={sortConfigStraight}
                  setSortConfig={setSortConfigStraight}
                  columnScales={columnScalesStraight}
                  setColumnScales={setColumnScalesStraight}
                  formattedColumns={formattedColumnsStraight}
                  setFormattedColumns={setFormattedColumnsStraight}
                  showDeltas={showDeltasStraight}
                  pivoted={pivotTables}
                  hideTwsColumn={false}
                  emptyMessage={
                    isSourceGroupedMode()
                      ? "No straight-line settings for this TWS bin and filters."
                      : "No straight-line settings for this day and filters."
                  }
                />
              </Show>
            </div>
            <Show
              when={
                props.mode === "fleet" &&
                showCopyStraightTable() &&
                !straightTableLoading() &&
                straightRowsForTable().length > 0
              }
            >
              <div class="copy-table-data-actions">
                <button
                  type="button"
                  class="copy-table-data-btn"
                  classList={{ "copy-table-data-btn-success": copySuccessStraightTable() }}
                  onClick={() => void copyStraightTableToClipboard()}
                >
                  {copySuccessStraightTable() ? "✓ Copied!" : "Copy Table Data"}
                </button>
              </div>
            </Show>
          </div>
          <Show when={!straightTableLoading() && straightRowsForTable().length > 0}>
            <div class="flex justify-between items-center mt-2 race-summary-table-actions">
              <div class="race-summary-actions-left">
                <label class="race-summary-deltas-checkbox">
                  <input
                    type="checkbox"
                    checked={showDeltasStraight()}
                    onChange={(e) => setShowDeltasStraight(e.currentTarget.checked)}
                  />
                  <span>Show deltas</span>
                </label>
              </div>
              <div class="race-summary-table-actions-right">
                <button class="btn" type="button" onClick={toggleFormattingStraight}>
                  {formattedColumnsStraight().length > 0 ? "Clear Formatting" : "Add formatting"}
                </button>
              </div>
            </div>
          </Show>
        </section>

        <section class="cheat-sheet-maneuvers-section">
          <h2 class={sectionTitleClass()}>{maneuverSectionTitle()}</h2>
          <Show when={props.mode !== "fleet"}>
            <div class="race-summary-dropdown-section cheat-sheet-dropdowns">
              <DropDownButton
                options={["TACK", "GYBE", "ROUNDUP", "BEARAWAY"]}
                defaultText={maneuverDisplay()}
                smallLabel="Maneuver"
                size="big"
                handleSelection={async (item) => {
                  changeManeuverType(item.toLowerCase() as DailyTrainingManeuverType);
                  const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
                  await logActivity(
                    project_id,
                    dataset_id,
                    "DailyTrainingSettingsSection.tsx",
                    props.pageLabel ?? "Training Settings",
                    `Maneuver changed to ${item}`
                  );
                }}
              />
            </div>
          </Show>
          <p class="cheat-sheet-table-explanation">{maneuverExplanation()}</p>
          <div
            class={props.mode === "fleet" ? "copy-table-hover-wrapper" : undefined}
            onMouseEnter={() => {
              if (props.mode === "fleet") {
                setIsHoveredManeuverTable(true);
              }
            }}
            onMouseLeave={() => {
              if (props.mode === "fleet") {
                setIsHoveredManeuverTable(false);
              }
            }}
          >
            <div class="data-table-container">
              <Show when={!maneuverTableLoading()} fallback={<TableLoadingPlaceholder />}>
                <SettingsTable
                  rows={maneuverRowsForTable}
                  rowLabelKey={rowLabelKey()}
                  columnOrder={maneuverColumnOrder()}
                  strictColumnOrder={true}
                  getHeader={getManeuverHeader}
                  sortConfig={sortConfigManeuver}
                  setSortConfig={setSortConfigManeuver}
                  columnScales={columnScalesManeuver}
                  setColumnScales={setColumnScalesManeuver}
                  formattedColumns={formattedColumnsManeuver}
                  setFormattedColumns={setFormattedColumnsManeuver}
                  showDeltas={showDeltasManeuver}
                  pivoted={pivotTables}
                  hideTwsColumn={isSourceGroupedMode()}
                  emptyMessage={
                    props.mode === "day"
                      ? "No maneuver settings for this TWS bin, maneuver type, and filters."
                      : "No maneuver settings for this day and filters."
                  }
                />
              </Show>
            </div>
            <Show
              when={
                props.mode === "fleet" &&
                showCopyManeuverTable() &&
                !maneuverTableLoading() &&
                maneuverRowsForTable().length > 0
              }
            >
              <div class="copy-table-data-actions">
                <button
                  type="button"
                  class="copy-table-data-btn"
                  classList={{ "copy-table-data-btn-success": copySuccessManeuverTable() }}
                  onClick={() => void copyManeuverTableToClipboard()}
                >
                  {copySuccessManeuverTable() ? "✓ Copied!" : "Copy Table Data"}
                </button>
              </div>
            </Show>
          </div>
          <Show when={!maneuverTableLoading() && maneuverRowsForTable().length > 0}>
            <div class="flex justify-between items-center mt-2 race-summary-table-actions">
              <div class="race-summary-actions-left">
                <label class="race-summary-deltas-checkbox">
                  <input
                    type="checkbox"
                    checked={showDeltasManeuver()}
                    onChange={(e) => setShowDeltasManeuver(e.currentTarget.checked)}
                  />
                  <span>Show deltas</span>
                </label>
              </div>
              <div class="race-summary-table-actions-right">
                <button class="btn" type="button" onClick={toggleFormattingManeuver}>
                  {formattedColumnsManeuver().length > 0 ? "Clear Formatting" : "Add formatting"}
                </button>
              </div>
            </div>
          </Show>
        </section>

        <Show when={showAccelerationSection()}>
          <section class="cheat-sheet-accelerations-section">
            <h2 class={sectionTitleClass()}>{accelerationSectionTitle()}</h2>
            <p class="cheat-sheet-table-explanation">{accelerationExplanation()}</p>
            <div
              class="copy-table-hover-wrapper"
              onMouseEnter={() => setIsHoveredAccelerationTable(true)}
              onMouseLeave={() => setIsHoveredAccelerationTable(false)}
            >
              <div class="data-table-container">
                <Show when={!accelerationTableLoading()} fallback={<TableLoadingPlaceholder />}>
                  <SettingsTable
                    rows={accelerationRowsForTable}
                    rowLabelKey={rowLabelKey()}
                    columnOrder={trainingSummaryAccelerationColumnOrder}
                    strictColumnOrder={true}
                    getHeader={getAccelerationHeader}
                    sortConfig={sortConfigAcceleration}
                    setSortConfig={setSortConfigAcceleration}
                    columnScales={columnScalesAcceleration}
                    setColumnScales={setColumnScalesAcceleration}
                    formattedColumns={formattedColumnsAcceleration}
                    setFormattedColumns={setFormattedColumnsAcceleration}
                    showDeltas={showDeltasAcceleration}
                    pivoted={pivotTables}
                    hideTwsColumn={isSourceGroupedMode()}
                    emptyMessage="No acceleration data for this day and filters."
                  />
                </Show>
              </div>
              <Show
                when={
                  showCopyAccelerationTable() &&
                  !accelerationTableLoading() &&
                  accelerationRowsForTable().length > 0
                }
              >
                <div class="copy-table-data-actions">
                  <button
                    type="button"
                    class="copy-table-data-btn"
                    classList={{ "copy-table-data-btn-success": copySuccessAccelerationTable() }}
                    onClick={() => void copyAccelerationTableToClipboard()}
                  >
                    {copySuccessAccelerationTable() ? "✓ Copied!" : "Copy Table Data"}
                  </button>
                </div>
              </Show>
            </div>
            <Show when={!accelerationTableLoading() && accelerationRowsForTable().length > 0}>
              <div class="flex justify-between items-center mt-2 race-summary-table-actions">
                <div class="race-summary-actions-left">
                  <label class="race-summary-deltas-checkbox">
                    <input
                      type="checkbox"
                      checked={showDeltasAcceleration()}
                      onChange={(e) => setShowDeltasAcceleration(e.currentTarget.checked)}
                    />
                    <span>Show deltas</span>
                  </label>
                </div>
                <div class="race-summary-table-actions-right">
                  <button class="btn" type="button" onClick={toggleFormattingAcceleration}>
                    {formattedColumnsAcceleration().length > 0 ? "Clear Formatting" : "Add formatting"}
                  </button>
                </div>
              </div>
            </Show>
          </section>
        </Show>
      </Show>
    </>
  );
}
